import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';

function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const navItems = [
    { path: '/', label: '🏠 首页', icon: '🏠' },
    { path: '/stories', label: '🔥 接力故事', icon: '🔥' },
    { path: '/create', label: '➕ 开新坑', icon: '➕' },
    { path: '/dashboard', label: '📊 我的', icon: '📊' },
  ];

  return (
    <div className="flex h-screen bg-gray-900 text-white">
      {/* 左侧边栏 - 导航 */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-gray-800 border-r border-gray-700
        transform transition-transform duration-300 ease-in-out
        lg:relative lg:transform-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-gray-700">
          <Link to="/" className="text-xl font-bold text-primary-400">
            📚 Novel Relay
          </Link>
        </div>

        {/* 导航菜单 */}
        <nav className="p-4 space-y-2">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex items-center px-4 py-3 rounded-lg transition-colors
                ${location.pathname === item.path 
                  ? 'bg-primary-600 text-white' 
                  : 'text-gray-300 hover:bg-gray-700'}
              `}
            >
              <span className="text-xl mr-3">{item.icon}</span>
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* 用户信息 */}
        {user && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700 bg-gray-800">
            <Link to={`/profile/${user.id}`} className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.username} className="w-full h-full rounded-full" />
                ) : (
                  <span className="text-lg">👤</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user.username}</p>
                <p className="text-xs text-gray-400 truncate">{user.email}</p>
              </div>
            </Link>
            {user.role === 'admin' && (
              <Link
                to="/admin"
                className="mt-2 block text-xs text-red-400 hover:text-red-300"
              >
                🛡️ 管理控制台
              </Link>
            )}
          </div>
        )}

        {!user && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700 bg-gray-800">
            <div className="flex space-x-2">
              <Link
                to="/login"
                className="flex-1 text-center py-2 bg-gray-700 hover:bg-gray-600 rounded"
              >
                登录
              </Link>
              <Link
                to="/register"
                className="flex-1 text-center py-2 bg-primary-600 hover:bg-primary-700 rounded"
              >
                注册
              </Link>
            </div>
          </div>
        )}
      </aside>

      {/* 中间内容区 */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* 顶部栏 - 移动端菜单按钮 */}
        <header className="h-16 bg-gray-800 border-b border-gray-700 flex items-center px-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-gray-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="ml-4 font-bold text-lg">Novel Relay</span>
        </header>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </div>
      </main>

      {/* 右侧边栏 - 快捷信息/通知 */}
      <aside className="hidden xl:block w-72 bg-gray-800 border-l border-gray-700 p-4 overflow-y-auto">
        <h3 className="font-bold text-gray-400 mb-4">📌 快捷操作</h3>
        
        <div className="space-y-4">
          <Link
            to="/create"
            className="block p-3 bg-primary-600 hover:bg-primary-700 rounded-lg text-center font-medium"
          >
            ✍️ 新建故事
          </Link>

          <div className="bg-gray-700 rounded-lg p-4">
            <h4 className="font-medium mb-2">📈 统计</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">我的故事</span>
                <span className="font-medium">0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">总章节</span>
                <span className="font-medium">0</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">总阅读</span>
                <span className="font-medium">0</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <h4 className="font-medium mb-2">🔥 热门榜单</h4>
            <p className="text-sm text-gray-400">加载中...</p>
          </div>
        </div>
      </aside>

      {/* 遮罩层 - 移动端 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}

export default MainLayout;
